import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

type User = {
  id: string;
  email: string;
  name: string;
  role: "customer" | "admin";
  emailVerified: boolean;
};

type AuthState = {
  user: User | null;
  accessToken: string | null;
  csrfToken: string | null;
  loading: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  register: (input: {
    name: string;
    email: string;
    password: string;
    turnstileToken?: string;
  }) => Promise<{ verifyToken?: string }>;
  logout: () => Promise<void>;
  setSession: (data: {
    accessToken: string;
    csrfToken: string;
    user: User;
  }) => void;
};

const AuthContext = createContext<AuthState | null>(null);
const TOKEN_KEY = "nr_access";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    localStorage.getItem(TOKEN_KEY),
  );
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const refreshQuery = useQuery({
    queryKey: ["auth", "refresh"],
    queryFn: async () => {
      try {
        const data = await api<{
          accessToken: string;
          csrfToken: string;
          user: User;
        }>("/api/auth/refresh", { method: "POST" });
        localStorage.setItem(TOKEN_KEY, data.accessToken);
        setAccessToken(data.accessToken);
        setCsrfToken(data.csrfToken);
        setUser(data.user);
        return data;
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setAccessToken(null);
        setUser(null);
        return null;
      }
    },
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (!accessToken) return;
    api<{ user: User }>("/api/auth/me", { token: accessToken })
      .then((d) => setUser(d.user))
      .catch(() => {
        /* refresh query handles recovery */
      });
  }, [accessToken]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      accessToken,
      csrfToken,
      loading: refreshQuery.isLoading,
      setSession: (data) => {
        localStorage.setItem(TOKEN_KEY, data.accessToken);
        setAccessToken(data.accessToken);
        setCsrfToken(data.csrfToken);
        setUser(data.user);
      },
      login: async (email, password, turnstileToken) => {
        const data = await api<{
          accessToken: string;
          csrfToken: string;
          user: User;
        }>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password, turnstileToken }),
        });
        localStorage.setItem(TOKEN_KEY, data.accessToken);
        setAccessToken(data.accessToken);
        setCsrfToken(data.csrfToken);
        setUser(data.user);
        await qc.invalidateQueries();
      },
      register: async (input) => {
        return api<{ message: string; verifyToken?: string }>(
          "/api/auth/register",
          {
            method: "POST",
            body: JSON.stringify(input),
          },
        );
      },
      logout: async () => {
        if (accessToken) {
          await api("/api/auth/logout", {
            method: "POST",
            token: accessToken,
          }).catch(() => undefined);
        }
        localStorage.removeItem(TOKEN_KEY);
        setAccessToken(null);
        setCsrfToken(null);
        setUser(null);
        await qc.invalidateQueries();
      },
    }),
    [user, accessToken, csrfToken, refreshQuery.isLoading, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
